/**
 * Optimization note!
 * DataCollection is used on every target component during data collection.
 * It can potentially be instanced 1000 times. So:
 * - Be very careful with computed properties
 * - Don't link components to the vuex store
 * (https://medium.com/@przemyslaw.jan.beigert/improve-destroy-performance-in-vue-47e212abe135)
 * - If needed, move heavy computed properties and vuex links to props to make sure these
 * are only calculated once and targets components destroy quickly.
 */
import { chain, find, get, size, sortBy, toNumber } from 'lodash';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import { OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';

export default {

  props: {
    sessionProgram: {
      type: Object,
      required: true,
    },

    target: {
      type: Object,
      required: true,
    },

    data: {
      type: Object,
      required: true,
    },

    activePromptLevels: {
      type: Array,
      required: true,
    },

    dense: {
      type: Boolean,
      default: false,
    },

    tiny: {
      type: Boolean,
      default: false,
    },

    disable: {
      type: Boolean,
      default: false,
    },

    workflowStatuses: {
      type: Object,
      default: () => ({}),
    },

    focused: {
      type: Boolean,
      default: false,
    },

    preventDuplicatesOnTrial: {
      type: Number,
      default: null,
    },

    sessionPaused: {
      type: Boolean,
      default: false,
    },
    sessionDurationOffset: {
      type: Date,
      default: null,
    },

  },

  mixins: [Targets, Interactions],

  computed: {

    parentTarget() {
      if (!this.target.parentTarget) {
        return null;
      }
      return chain(this.sessionProgram)
        .get(['program', 'targets'])
        .find({ id: this.target.parentTarget })
        .value();
    },

    targetWorkflow() {
      return get(
        this.getAdjustedWorkflow(
          this.workflowStatuses,
          get(this.sessionProgram, 'program'),
          this.target,
          this.parentTarget,
        ),
        this.target.status,
      );
    }, // targetWorkflow()

    isDisabled() {
      if (this.disable || (this.preventDuplicatesOnTrial === null)) {
        return this.disable;
      }

      // Disable if we already have data for this target at this trial
      return chain(this.data)
        .get(['data'])
        .filter({ target: get(this.target, 'id') })
        .filter({ trial: this.preventDuplicatesOnTrial })
        .size()
        .value() > 0;
    }, // isDisabled()

    isDimmed() {
      if (this.dimmed || (this.preventDuplicatesOnTrial === null)) {
        return this.dimmed;
      }

      // Dim if we already have data for this target at this trial
      return chain(this.data)
        .get(['data'])
        .filter({ target: get(this.target, 'id') })
        .filter({ trial: this.preventDuplicatesOnTrial })
        .size()
        .value() > 0;
    }, // isDisabled()

    hasDiscriminativeStimulus() {
      return !!get(this.target, 'discriminativeStimulus');
    }, // hasDiscriminativeStimulus

    targetWorkflowMinTrials() {
      return this.getTargetWorkflowProperty('minTrials') || null;
    }, // targetWorkflowMinTrials()

    hasReachedMinTrials() {
      if (this.targetWorkflowMinTrials === null) {
        return false;
      }

      const trialCount = chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .filter('trial')
        .size()
        .value();

      return trialCount >= this.targetWorkflowMinTrials;
    }, // hasReachedMinTrials()

    discriminativeStimulus() {
      return this.target.discriminativeStimulus
        || this.sessionProgram.program.discriminativeStimulus
        || null;
    }, // discriminativeStimulus()

    previousPromptLevelStyle() {
      const previousTrial = chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .last()
        .value();
      if (!previousTrial) {
        const previousSessionPromptLevelId = get(this.target, 'previousPromptLevel', null);
        const previousSessionPromptLevel = find(this.programPrompts, { id: previousSessionPromptLevelId });
        if (previousSessionPromptLevel) {
          return this.getPromptLevelStyle(previousSessionPromptLevel);
        }

        return this.getPromptLevelStyle(null);
      }
      const previousPromptLevel = find(this.programPrompts, { id: get(previousTrial, 'promptLevel') }) || null;

      return this.getPromptLevelStyle(previousPromptLevel, get(previousTrial, 'scoreValue'));
    }, // previousPromptLevelStyle()

    promptLevelTemplate() {
      return this.getActivePromptLevelTemplateId(get(this.target, 'promptLevelTemplate'))
        || this.getActivePromptLevelTemplateId(get(this.parentTarget, 'defaultPromptLevelTemplate'))
        || this.sessionProgram.program.promptLevelTemplate;
    }, // promptLevelTemplate()

    programPromptLevelTemplate() {
      return chain(this.activePromptLevels)
        .find({ id: this.promptLevelTemplate })
        .value();
    }, // programPromptLevelTemplate()

    programPrompts() {
      const promptLevels = get(this.programPromptLevelTemplate, 'promptLevels', []);
      const customized = this.getCustomizedProgramPromptLevels(this.promptLevelTemplate);

      if (customized) {
        return this.applyCustomizedPromptLevels(promptLevels, customized);
      }

      return promptLevels;
    }, // programPrompts()

    subTargetProgramPrompts() {
      const promptLevelTemplateId = this.target?.defaultPromptLevelTemplate
        || this.sessionProgram?.program?.promptLevelTemplate;

      const promptLevels = chain(this.activePromptLevels)
        .find({ id: promptLevelTemplateId })
        .get('promptLevels')
        .value() || [];

      const customized = this.getCustomizedProgramPromptLevels(promptLevelTemplateId);

      if (customized) {
        return this.applyCustomizedPromptLevels(promptLevels, customized);
      }

      return promptLevels;
    }, // programPrompts()

    hasMultiplePromptLevels() {
      return size(this.programPrompts) !== 1;
    }, // hasMultiplePromptLevels()

    targetIsOpen() {
      return !this.disable
        && ['probe', 'acquisition', 'mastered'].includes(
          this.getTargetWorkflowProperty('type'),
        );
    }, // targetIsOpen()

  },

  methods: {

    getTargetWorkflowProperty(property, defaultValue = '') {
      return get(this.targetWorkflow, property, defaultValue);
    }, // getTargetWorkflowProperty()

    getActivePromptLevelTemplateId(templateId) {
      return chain(this.activePromptLevels)
        .find({ id: templateId })
        .get('id', null)
        .value();
    }, // getActivePromptLevelTemplateId()

    notifyDeletedPromptLevelTemplate() {
      if (!this.getActivePromptLevelTemplateId(this.sessionProgram.program.promptLevelTemplate)) {
        this.notifyWarning(this.$t('run.collectData.invalidPromptLevelTemplateSelected'));
        return;
      }

      if (this.target.promptLevelTemplate && !this.getActivePromptLevelTemplateId(this.target.promptLevelTemplate)) {
        this.notifyWarning(this.$t('sessions.trialOverview.notifyDeletedPromptLevelTemplate'));
      }
    }, // notifyDeletedPromptLevelTemplate()

    getCustomizedProgramPromptLevels(promptLevelTemplateId) {
      const customized = get(this.sessionProgram, ['program', 'customizedPromptLevels'], null);
      if (!customized || customized.promptLevelId !== promptLevelTemplateId) {
        return null;
      }
      return customized;
    },

    applyCustomizedPromptLevels(promptLevels, customizedProgramPromptLevels) {
      if (!customizedProgramPromptLevels) {
        return promptLevels;
      }

      const { visiblePromptLevels = [], hiddenPromptLevels = [] } = customizedProgramPromptLevels;

      const hiddenIds = new Set(hiddenPromptLevels.map(({ id }) => id));

      const visibleOrderById = visiblePromptLevels.reduce((acc, { id, order }) => {
        acc[id] = order;
        return acc;
      }, {});

      const maxVisibleOrder = visiblePromptLevels.reduce(
        (max, { order }) => Math.max(max, order || 0),
        0,
      );

      let nextOrder = maxVisibleOrder + 1;

      const modifiedPrompts = promptLevels.reduce((acc, pl) => {
        if (hiddenIds.has(pl.id)) {
          return acc;
        }

        // eslint-disable-next-line no-plusplus
        const order = visibleOrderById[pl.id] ?? nextOrder++;

        acc.push({ ...pl, order });
        return acc;
      }, []);

      return sortBy(modifiedPrompts, 'order');
    },

    calculateTrialsSummary(trials) {
      const outcomeType = this.programPromptLevelTemplate?.outcomeMeasurement;
      if (outcomeType === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE) {
        const averageScore = chain(trials)
          .map(({ scoreValue }) => toNumber(scoreValue))
          .mean()
          .round(1)
          .value();

        return `${averageScore}`;
      }

      const trialSuccesses = chain(trials)
        .map(({ scoreValue }) => toNumber(scoreValue))
        .sum()
        .divide(100)
        .value();
      return `${trialSuccesses} / ${size(trials)}`;
    }, // calculateTrialsSummary()

  },
};
